/**
 * Liquid Cooling section descriptor (#997 — Story 4.2).
 *
 * The real, live `SectionDescriptor` for the Liquid Cooling (CDU) dynamic
 * section. It mirrors the Racks descriptor (Story 3.3) but binds the ACTUAL
 * Liquid Cooling per-CDU field set, table, mapper, REST contract, and
 * attachment card, so the shared descriptor engine can drive Liquid Cooling at
 * byte-identical parity with the pre-engine bespoke implementation (Story 4.2
 * golden masters are the oracle).
 *
 * Field source of truth: `LIQUID_COOLING_FIELDS` (`liquid-cooling.fields.ts`)
 * plus the editor slice at
 * `LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS[3..]` — every per-unit field is
 * typed here (text / multilineText / dropdown / yesNo) to match the real
 * controls in `LiquidCoolingCduEditFormContent.tsx`. `name` (user-defined
 * instance name) and `status` (Existing/New) are the engine's identity fields
 * and are handled by the engine list chrome, so they are NOT per-unit
 * descriptor `fields` (they are added back only on the Summary surface via the
 * identity-field label bridge in `liquidCoolingDescriptorSummary`).
 *
 * `lcAdditionalInformationSfn` is a PER-UNIT "Additional Information" note on
 * `LiquidCoolingCduConfiguration` (#997 Phase 3 semantics), so it lives on the
 * per-unit fields list and is declared via `additionalInfoField`. The shared
 * request-level field `lcLiquidToChipDesired` is the ONLY shared key.
 *
 * NFR7: the table (`request_liquid_cooling_cdus`), mapper
 * (`liquid-cooling-cdu-configuration.mapper`), and permission config are
 * read-only; this descriptor only DECLARES the binding.
 */

import { locationTagDescriptorField } from './sectionDescriptor.types';
import {
  LIQUID_COOLING_CDU_TABLE_COLUMN_ALIGN,
  formatLiquidCoolingCduTableCell,
  getLiquidCoolingCduColumnSubline,
  liquidCoolingCduColumnUsesDoubleLine,
  liquidCoolingCduTableColumnsForLayout,
  liquidCoolingCduTableHeaderLabel,
} from '../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import {
  LIQUID_COOLING_CDU_ADD_BUTTON_LABEL,
  LIQUID_COOLING_CDU_CONFIGURATIONS_LIST_TITLE,
  LIQUID_COOLING_CDU_DELETE_CONFIRM_MESSAGE,
  LIQUID_COOLING_CDU_DELETE_CONFIRM_TITLE,
  LIQUID_COOLING_CDU_DISCARD_UNSAVED_DISCARD_LABEL,
  LIQUID_COOLING_CDU_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  LIQUID_COOLING_CDU_DISCARD_UNSAVED_MESSAGE,
  LIQUID_COOLING_CDU_DISCARD_UNSAVED_TITLE,
  LIQUID_COOLING_CDU_EDIT_ALL_UNITS_LABEL,
  LIQUID_COOLING_CDU_EDIT_DELETE_LABEL,
  LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_NEW,
  LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_LABEL,
  LIQUID_COOLING_CDU_EDIT_SAVE_LABEL,
  LIQUID_COOLING_CDU_EMPTY_STATE_TITLE,
} from '../../constants/liquidCoolingCduConfigurationsSubsectionCopy';
import { LIQUID_COOLING_CDU_RACK_OR_ROW_OPTIONS } from '../../constants/liquidCoolingCduRequirementsUi';
import { REDUNDANCY_OPTIONS } from '../../constants/redundancy';
import { SECTION_IDS } from '../../constants/sectionIds';
import {
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { LIQUID_COOLING_FIELDS } from '../fields/steps/liquid-cooling.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { LiquidCoolingCduTableColumnKey } from '../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import type { LiquidCoolingCduConfiguration } from '../../types/liquidCoolingCdu';

/**
 * Per-field render type + validation + dropdown options, keyed by dataField.
 * Every non-identity per-unit key covered by the descriptor has an entry here;
 * anything missing falls back to `text`. Types match the real Liquid Cooling
 * controls (see `LiquidCoolingCduEditFormContent`).
 */
interface LcFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const LC_FIELD_TYPES: Readonly<Record<string, LcFieldTypeSpec>> = {
  // Per-unit CDU details (order preserved by LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS[3..])
  lcCduType: {
    type: 'dropdown',
    options: LIQUID_COOLING_CDU_RACK_OR_ROW_OPTIONS,
    validation: { options: LIQUID_COOLING_CDU_RACK_OR_ROW_OPTIONS },
  },
  lcDescription: { type: 'text' },
  lcVertivSuppliesRowManifolds: { type: 'yesNo' },
  lcVertivSuppliesRackManifolds: { type: 'yesNo' },
  lcCduRedundancy: {
    type: 'dropdown',
    options: REDUNDANCY_OPTIONS,
    validation: { options: REDUNDANCY_OPTIONS },
  },
  lcLeakDetectionApproach: { type: 'text' },
  // Per-unit "Additional Information" note
  lcAdditionalInformationSfn: { type: 'multilineText' },
  // Shared request-level field
  lcLiquidToChipDesired: { type: 'yesNo' },
};

/** Registry lookup: dataField → RequestFieldDefinition (for label / subsection). */
const LC_FIELDS_BY_DATA_FIELD = new Map(
  LIQUID_COOLING_FIELDS.map(field => [field.dataField, field]),
);

/** Identity fields the engine list chrome owns; NOT in per-unit `fields`. */
const LC_IDENTITY_KEYS = new Set(['name', 'status']);

/** Shared field key set (a single `lcLiquidToChipDesired`). */
const LC_SHARED_FIELD_KEY_SET = new Set<string>(
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
);

/**
 * Per-unit descriptor field dataField keys, in editor order. Sourced from
 * `LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS` minus identity fields (`name`,
 * `status`) minus the shared key (`lcLiquidToChipDesired`), plus the note field
 * (`lcAdditionalInformationSfn`) appended.
 */
const PER_UNIT_DATA_FIELD_KEYS: readonly string[] = [
  ...LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS.filter(
    field =>
      !LC_IDENTITY_KEYS.has(field.key) &&
      !LC_SHARED_FIELD_KEY_SET.has(field.key),
  ).map(field => field.key),
  'lcAdditionalInformationSfn',
];

function buildDescriptorField(dataField: string): SectionDescriptorField {
  if (dataField === 'locationTagId') {
    return locationTagDescriptorField('liquid-cooling', 'liquidCoolingProject');
  }
  const registryField = LC_FIELDS_BY_DATA_FIELD.get(dataField);
  if (!registryField) {
    throw new Error(
      `LIQUID_COOLING_DESCRIPTOR: no LIQUID_COOLING_FIELDS entry for ${dataField}`,
    );
  }
  const spec = LC_FIELD_TYPES[dataField] ?? { type: 'text' };
  return {
    ...registryField,
    type: spec.type,
    options: spec.options,
    validation: spec.validation,
  };
}

/** Typed per-unit descriptor fields (7). */
const LIQUID_COOLING_DESCRIPTOR_PER_UNIT_FIELDS: readonly SectionDescriptorField[] =
  PER_UNIT_DATA_FIELD_KEYS.map(buildDescriptorField);

/** Typed shared descriptor fields (1: `lcLiquidToChipDesired`). */
const LIQUID_COOLING_DESCRIPTOR_SHARED_FIELDS: readonly SectionDescriptorField[] =
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS.map(buildDescriptorField);

/** Full descriptor `fields` = per-unit + shared. */
const LIQUID_COOLING_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] = [
  ...LIQUID_COOLING_DESCRIPTOR_PER_UNIT_FIELDS,
  ...LIQUID_COOLING_DESCRIPTOR_SHARED_FIELDS,
];

/**
 * Snake_case columns the Liquid Cooling section persists — the exact key set
 * emitted by `liquid-cooling-cdu-configuration.mapper.toEntity` and (excluding
 * identity/audit columns) the `request_liquid_cooling_cdus` migration columns.
 * The `descriptorDbBinding` guardrail joins this list to both.
 */
export const LIQUID_COOLING_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'status',
  'name',
  'location_tag_id',
  'lc_cdu_type',
  'lc_vertiv_supplies_row_manifolds',
  'lc_vertiv_supplies_rack_manifolds',
  'lc_cdu_redundancy',
  'lc_leak_detection_approach',
  'lc_description',
  'lc_additional_information_sfn',
  'display_order',
];

/**
 * Liquid Cooling CDU list projection (#997 Story 6.8 — parity by reuse). Delegates
 * to the product-approved bespoke table (`liquidCoolingCduListUtils`: NAME |
 * TYPE | LEAK | REDUNDANCY, sublines, double-line cells). Engine rows carry
 * identity as `instanceName`/`instanceStatus` (stripped from the CDU shape by
 * `liquidCoolingCduToDescriptorInstance`), so adapt the row back before the
 * CDU-shaped formatters run — the exact `coolingSystemRowForList` pattern.
 */
const liquidCoolingCduRowForList = (
  row: Record<string, unknown>,
): LiquidCoolingCduConfiguration => {
  const unit = row as unknown as LiquidCoolingCduConfiguration;
  return {
    ...unit,
    name:
      unit.name ?? (row.instanceName as LiquidCoolingCduConfiguration['name']),
    status:
      unit.status ??
      (row.instanceStatus as LiquidCoolingCduConfiguration['status']),
  };
};

const LIQUID_COOLING_CDU_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  columnsForLayout: liquidCoolingCduTableColumnsForLayout,
  headerLabel: (column, layout) =>
    liquidCoolingCduTableHeaderLabel(
      column as LiquidCoolingCduTableColumnKey,
      layout,
    ),
  formatCell: (row, column) =>
    formatLiquidCoolingCduTableCell(
      liquidCoolingCduRowForList(row),
      column as LiquidCoolingCduTableColumnKey,
    ),
  columnAlign: LIQUID_COOLING_CDU_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getLiquidCoolingCduColumnSubline(
      column as LiquidCoolingCduTableColumnKey,
      liquidCoolingCduRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    liquidCoolingCduColumnUsesDoubleLine(
      column as LiquidCoolingCduTableColumnKey,
      layout,
    ),
};

/** The canonical Liquid Cooling descriptor. */
export const LIQUID_COOLING_DESCRIPTOR: SectionDescriptor = {
  id: 'liquid-cooling',
  stepId: 'liquid-cooling',
  labels: {
    entitySingular: 'Liquid Cooling',
    entityPlural: 'Liquid Cooling',
    addButtonLabel: LIQUID_COOLING_CDU_ADD_BUTTON_LABEL,
    emptyStateTitle: LIQUID_COOLING_CDU_EMPTY_STATE_TITLE,
    editFormTitle: LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: LIQUID_COOLING_CDU_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.8, mirror Cooling 6.6 / Power Distribution
    // 6.7): bind the bespoke hand-authored copy so the engine chrome is
    // byte-identical to the pre-swap subsection, not template-coincident.
    editSaveLabel: LIQUID_COOLING_CDU_EDIT_SAVE_LABEL,
    editSaveDraftLabel: LIQUID_COOLING_CDU_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: LIQUID_COOLING_CDU_EDIT_DELETE_LABEL,
    deleteConfirmTitle: LIQUID_COOLING_CDU_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: LIQUID_COOLING_CDU_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: LIQUID_COOLING_CDU_EDIT_ALL_UNITS_LABEL,
    editDiscardTitle: LIQUID_COOLING_CDU_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: LIQUID_COOLING_CDU_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: LIQUID_COOLING_CDU_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel:
      LIQUID_COOLING_CDU_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: LIQUID_COOLING_CDU_EDIT_HEADER_TITLE_NEW,
  },
  cardinality: 'many',
  fields: LIQUID_COOLING_DESCRIPTOR_FIELDS,
  permissionRef: { rbacAnchorDataField: 'lcCduType' },
  summaryOrder: LIQUID_COOLING_DESCRIPTOR_FIELDS.map(field => field.dataField),
  pdfLayout: {
    stepId: 'liquid-cooling',
    anchor: 'Liquid Cooling CDU Information',
    perUnitFieldGroupKeys: ['liquidCoolingCduInfoFields'],
  },
  sharedFieldKeys: LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
  // The live LC editor renders two per-CDU attachment cards, both scoped by
  // `resolveRepeatableInstanceAttachmentSectionId(base, configurationId)`:
  // the details card at SECTION_IDS.LIQUID_COOLING_PROJECT and the Notes
  // (Additional-Info) card at SECTION_IDS.LIQUID_COOLING_NOTES (#997 B1 / PD-2,
  // per-unit-scoping the Notes attachments to match the per-CDU note text and
  // the LU/PD/Cooling Obs-7 fix). `_NOTES` is last to match the `_ADDITIONAL`-last
  // convention (the descriptor engine derives the additional section id from the
  // final entry). Consumed by the create-time staged-file move in
  // `descriptorEngineAdapter` `onCreateInstance`; kept in lockstep with
  // `LIQUID_COOLING_CDU_REPEATABLE_ATTACHMENT_BASE_SECTIONS` (guard test #997 B3).
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.LIQUID_COOLING_PROJECT,
    SECTION_IDS.LIQUID_COOLING_NOTES,
  ],
  additionalInfoField: 'lcAdditionalInformationSfn',
  listColumns: LIQUID_COOLING_CDU_LIST_COLUMNS,
  offlineBinding: {
    restResource: 'liquid-cooling-cdus',
    // Liquid Cooling is the SIXTH (final) section wired onto the engine
    // save/flush path (#997 Story 6.8). It opts in explicitly via the delegating
    // flush shim `flushLiquidCoolingCduDescriptorEditSession` (see
    // `resolveDelegatedFlushBinding`).
    delegatedFlush: true,
    reorderIdsBodyKey: 'liquidCoolingCduIds',
    persistedInstancesKey: 'liquidCoolingCduConfigurations',
    instanceIdOpField: 'liquidCoolingCduId',
    reorderIdsOpField: 'liquidCoolingCduIds',
  },
  dbBinding: {
    table: 'request_liquid_cooling_cdus',
    columns: LIQUID_COOLING_DESCRIPTOR_DB_COLUMNS,
  },
};
